import { usePage } from "@inertiajs/react"
import FooterBackdrop from "@/components/FooterBackdrop"
import { AccreditationBadges } from "@/components/gisela/AccreditationBadges"
import { NewsletterSignup } from "@/components/gisela/NewsletterSignup"
import { useLocalePath, useSpanishFirstPath } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

// Footer for Gisela's site: her identity, quick links, and the Sanctuary base
// as her credential (NAP for local SEO) linking across to the venue site.
// Plain navy, or — when the admin has chosen a photo in the site-wide
// "Footer background" slot — that photo under the same navy film as a page
// hero (see HeroBand), so the small white copy still reads on any picture.
export default function GiselaFooter() {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_nav
  const sanctuaryUrl = props.brand_urls.sanctuary
  const phone = props.current_brand.phone
  const lp = useLocalePath()
  const spanishFirst = useSpanishFirstPath()

  // The headline tier of her conditions menu, from the database
  // (admin-manageable) and already in the visitor's language. The second tier
  // stays to the nav dropdown's flyout and the index page — the footer would
  // be a wall of links otherwise.
  const conditionLinks = (props.gisela_condition_menu?.[0]?.items ?? []).map((c) => ({
    label: c.label,
    href: lp(c.path),
  }))

  // Her therapies, from the same source as the nav's second dropdown. The
  // whole list fits: there are five of them, not thirty.
  const therapyLinks = (props.gisela_therapy_menu?.[0]?.items ?? []).map((therapy) => ({
    label: therapy.label,
    href: lp(therapy.path),
  }))


  // Static links drop out when their page is switched off in the admin —
  // filtered on the raw English path, before lp() localizes it. The
  // condition/therapy links come from the published-only menus and need no
  // filter of their own.
  const hidden = props.hidden_paths
  const staticLink = (label: string, path: string) =>
    hidden.includes(path) ? [] : [{ label, href: lp(path) }]

  // No standalone OCD row: it is the first condition link, so listing it
  // twice would give the footer two links to the same page.
  const links = [
    ...staticLink(t.help_with, "/what-i-can-help-with"),
    ...conditionLinks,
    ...staticLink(t.therapies, "/therapies-i-offer"),
    ...therapyLinks,
    ...staticLink(t.online_therapy, "/online-therapy"),
    ...staticLink(t.fees, "/fees"),
    // Her Spanish landing page is linked in Spanish, at its Spanish URL, from
    // both versions of the footer — see useSpanishFirstPath. Still filtered on
    // the English path, which is what the admin's switches name.
    ...(hidden.includes("/therapy-in-spanish")
      ? []
      : [ { label: t.therapy_in_spanish_native, href: spanishFirst("/therapy-in-spanish")! } ]),
    ...staticLink(t.about, "/about"),
    ...staticLink(t.blog, "/blog"),
    ...staticLink(t.contact, "/contact"),
    ...staticLink(t.book_call, "/book"),
  ]

  return (
    <footer className="relative overflow-hidden bg-navy text-white">
      <FooterBackdrop />
      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-3">
          {/* Identity — same house-mark lockup as the nav (see GiselaNav). The
              explicit "Sanctuary Therapy Centre" naming lives in the base
              column to the right, so the mark alone is enough here. */}
          <div>
            <div className="flex items-center gap-3">
              <img src="/sanctuary-favicon.png" alt="" aria-hidden className="h-9 w-9 shrink-0" />
              <div>
                <p className="font-display text-lg text-white/90">Gisela Ramirez</p>
                <p className="mt-0.5 text-sm text-white/60">{t.tagline}</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/60">{t.footer_line}</p>
            {phone && (
              <p className="mt-4 text-sm">
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="text-white/90 no-underline transition-colors hover:text-white"
                >
                  {t.ring_now} · {phone}
                </a>
              </p>
            )}

            {/* Accreditation badges */}
            <div className="mt-6">
              <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
                {t.accreditations}
              </h6>
              <AccreditationBadges />
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
              {t.navigate}
            </h6>
            <ul className="m-0 list-none space-y-2 p-0 text-sm text-white/70">
              {links.map(({ label, href }) => (
                <li key={href}>
                  <a href={href} className="text-white/70 no-underline transition-colors hover:text-white">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* The base — venue as credential, NAP for local SEO. The official
              lockup, cut for a dark ground, is the one place her site shows
              the parent brand in full, and the one link to it: the nav keeps
              to the symbol so nobody mistakes which site they are on. */}
          <div>
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
              {t.in_person_at}
            </h6>
            <a href={sanctuaryUrl} className="mb-5 block w-44">
              <img src="/logos/sanctuary-lockup-light.png" alt="Sanctuary Therapy Centre" className="h-auto w-full" loading="lazy" decoding="async" />
            </a>
            <p className="text-sm leading-relaxed text-white/70">
              <a
                href={`${sanctuaryUrl}/prestwich`}
                className="text-white/90 no-underline transition-colors hover:text-white"
              >
                Prestwich
              </a>
              <br />
              Oakhill Court, 171 Bury New Road
              <br />
              Prestwich, Manchester M25 9ND
              <br />
              <a href={lp("/therapy-in-prestwich")} className="text-white/70 transition-colors hover:text-white">
                {t.therapy_in_town.replace("%{name}", "Prestwich")}
              </a>
            </p>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              <a
                href={`${sanctuaryUrl}/hebden-bridge`}
                className="text-white/90 no-underline transition-colors hover:text-white"
              >
                Hebden Bridge
              </a>
              <br />
              36 Market Street
              <br />
              Hebden Bridge HX7 6AA
              <br />
              <a href={lp("/therapy-in-hebden-bridge")} className="text-white/70 transition-colors hover:text-white">
                {t.therapy_in_town.replace("%{name}", "Hebden Bridge")}
              </a>
            </p>
            {/* For the therapists who read her site: the venue's room hire. */}
            <p className="mt-4 text-sm leading-relaxed">
              <a
                href={`${sanctuaryUrl}/rent-a-room`}
                className="text-white/70 no-underline transition-colors hover:text-white"
              >
                {t.room_hire}
              </a>
            </p>
          </div>
        </div>

        {props.features.newsletter_signup && (
          <div className="mt-12 max-w-md border-t border-white/10 pt-8">
            <NewsletterSignup variant="footer" />
          </div>
        )}

        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-white/40">
          © {new Date().getFullYear()} Gisela Ramirez · {t.tagline}
        </div>
      </div>
    </footer>
  )
}
